// @ts-strict-ignore
import {
  formatDate,
  isDefinedValue,
  parseDate,
  renamePropertyOld,
} from 'common/query-builder/cohorts-query-builder/util';
import {
  isArithmeticOperatorNode,
  isLiteralOperandNode,
  isOperandNode,
  isOperatorNode,
  isPropertyTypeOperandNode,
  isTypeCastOperatorNode,
} from 'common/query-builder/cohorts-query-builder/util/expression-tree';
import {
  filterOperatorFromOperator,
  isInvalidStagedFilterClause,
  typeFromTypeCastOperator,
  valueToSerializedDatetime,
} from 'common/query-builder/cohorts-query-builder/util/staged-cohort';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {profilePropertyIsInvalidForDataGroup} from 'common/query-builder/query-builder/util/data-groups';

import {BehaviorWindow} from '../behaviors/behavior-window';
import {DEFAULT_FILTER_OPERATOR, DEFAULT_RELATIVE_NUM_DAYS, DEFAULT_WINDOW_UNIT, MAX_LABEL_ENTRIES} from '../constants';
import {
  BooleanOperator,
  DatetimeOperator,
  ListOperator,
  NumberOperator,
  StringOperator,
  Type,
} from '../enums/builder-enums';
import {WindowUnit} from '../enums/expression-enums';
import {SerializedBehaviorWindow, SerializedDatetime} from '../types/serialized-cohort-types';
import {SerializedPropertyFilter} from '../types/serialized-staged-cohort-types';
import {Trackable} from '../types/tracking';
import {ArithmeticOperator, LogicalOperator, Property, TypeCastOperator} from '../enums/expression-enums';
import {ExpressionNode} from '../expression/expression-node';
import {OperandNode} from '../expression/operand-node';
import {OperatorNode} from '../expression/operator-node';
import {conditionalNegateExpression, expressionToClauseRoot} from '../expression/utils';
import {FilterLabelPart, FilterOperator, StagedPropertyFilterClause} from '../types/builder-state-types';
import {FilterValue, Operator, Value} from '../types/expression-types';

interface PropertyFilterOptions {
  propertyType: Property;
  joinPropertyType?: string;
  scdEvent?: string;
  property: string | string[];
  type: Type;
  operator: FilterOperator;
  value: FilterValue;
  negated?: boolean;
  dataGroupId?: string;
  customPropertyId?: string | number;
}

export class PropertyFilter implements Trackable {
  public static deserialize(options: SerializedPropertyFilter): PropertyFilter {
    if (!options) {
      return null;
    }

    const clause = new PropertyFilter(options);
    return options.stagedFilterClause ? clause.setStagedFilterClause(options.stagedFilterClause) : clause;
  }

  // TODO opslead
  public static createFromParts({tree}: {tree: OperatorNode}): PropertyFilter {
    if (!tree) {
      return null;
    }

    const propertyType = tree.find<Property, OperandNode>((node) => node.operand.property, isPropertyTypeOperandNode);

    const propertyValue = tree.find<FilterValue, OperandNode>(
      (node) => node.operand.value,
      (node) =>
        isOperandNode(node) && (node.operand.property === Property.User || node.operand.property === Property.Event),
    );

    let customPropertyId = null;
    let property = null;
    if (typeof propertyValue === `object` && !Array.isArray(propertyValue)) {
      customPropertyId = propertyValue[`customPropertyId`];
      property = customPropertiesStore.value[customPropertyId]?.name;
    } else {
      property = propertyValue as string;
    }

    // TODO (duc): clean up the flow to pass and extract dataGroupId
    const dataGroupId = tree.find<string, OperandNode>(
      (node) => node.operand.data_group_id,
      (node) =>
        isOperandNode(node) &&
        node.operand.data_group_id &&
        (node.operand.property === Property.User || node.operand.property === Property.Event),
    );

    const joinPropertyType = tree.find<string, OperandNode>(
      (node) => node.operand.join_property_type,
      (node) =>
        isOperandNode(node) &&
        node.operand.join_property_type &&
        (node.operand.property === Property.User || node.operand.property === Property.Event),
    );

    const scdEvent = tree.find<string, OperandNode>(
      (node) => node.operand.scdEvent,
      (node) =>
        isOperandNode(node) &&
        node.operand.scdEvent &&
        (node.operand.property === Property.User || node.operand.property === Property.Event),
    );

    const typeCastOperator = tree.find<TypeCastOperator, OperatorNode>(
      (node) => node.operator as TypeCastOperator,
      isTypeCastOperatorNode,
    );
    let type = typeCastOperator ? typeFromTypeCastOperator(typeCastOperator) : null;

    const values: Array<Value | Value[]> = tree.traverse<Value | Value[], OperandNode>(
      (node) => node.operand.value,
      isLiteralOperandNode,
    );
    const firstValue = values[0];
    const arithmeticOperators = tree.traverse<ArithmeticOperator, OperatorNode>(
      (node) => node.operator as ArithmeticOperator,
      isArithmeticOperatorNode,
    );

    let negated = !!tree.find<OperatorNode, OperatorNode>(
      (node) => node,
      (node) => isOperatorNode(node) && node.operator === LogicalOperator.Not,
    );

    let filterOperator;
    if (
      values.length === 2 &&
      arithmeticOperators.includes(ArithmeticOperator.LessThanEquals) &&
      arithmeticOperators.includes(ArithmeticOperator.GreaterThanEquals)
    ) {
      filterOperator = NumberOperator.Between;
    } else if (values.length > 1) {
      // multiple string values, old format (using a binary tree of ORs)
      filterOperator = arithmeticOperators.includes(ArithmeticOperator.Equals)
        ? StringOperator.Equals
        : StringOperator.DoesNotEqual;
    } else {
      if (Array.isArray(firstValue) && arithmeticOperators.includes(ArithmeticOperator.EqualsAny)) {
        // multiple string values, new format (using equals_any)
        if (negated) {
          filterOperator = StringOperator.DoesNotEqual;
          negated = false;
        } else {
          filterOperator = StringOperator.Equals;
        }
      } else {
        filterOperator = filterOperatorFromOperator(arithmeticOperators[0], type, firstValue as Value);
      }
    }

    let value: FilterValue;
    if (
      values.length === 1 &&
      Array.isArray(firstValue) &&
      arithmeticOperators.includes(ArithmeticOperator.EqualsAny)
    ) {
      value = firstValue;
    } else {
      value = values as Value[];
    }
    if (filterOperator === StringOperator.Set || filterOperator === StringOperator.NotSet) {
      // for defined operator, we don't add typecast to prevent undefined values from being typecast to defined values
      value = null;
      type = Type.String;
    } else if (arithmeticOperators[0] === ArithmeticOperator.Datetime) {
      // special handling for datetimes -- we convert serialized datetime into UI-friendly values
      type = Type.Datetime;
      const {from_date: fromDate, to_date: toDate} = firstValue as SerializedDatetime;
      switch (filterOperator) {
        case DatetimeOperator.After:
        case DatetimeOperator.On:
          value = fromDate as string;
          break;
        case DatetimeOperator.Between:
          value = [fromDate as string, toDate as string];
          break;
        case DatetimeOperator.Relative:
        case DatetimeOperator.RelativeFuture:
          value = firstValue;
          break;
        case DatetimeOperator.RelativeBeforeInPast:
          value = {window: (firstValue as SerializedDatetime).to_date as SerializedBehaviorWindow};
          break;
        default:
          break;
      }
    } else if (
      values.length === 1 &&
      ![StringOperator.Equals, StringOperator.DoesNotEqual].includes(filterOperator as StringOperator)
    ) {
      value = firstValue;
    }

    return new PropertyFilter({
      ...(dataGroupId ? {dataGroupId} : {}),
      ...(joinPropertyType ? {joinPropertyType} : {}),
      ...(scdEvent ? {scdEvent} : {}),
      negated,
      operator: filterOperator,
      property,
      propertyType,
      type,
      value,
      ...(customPropertyId ? {customPropertyId} : {}),
    });
  }

  public declare readonly propertyType: Property;
  public declare readonly joinPropertyType: string;
  public declare readonly scdEvent: string;
  public declare readonly property: string | string[];
  public declare readonly type: Type;
  public declare readonly operator: FilterOperator;
  public declare readonly value: FilterValue;
  public readonly negated = false;
  public declare readonly dataGroupId: string;
  public declare readonly customPropertyId: string | number;

  private stagedFilterClause: StagedPropertyFilterClause = null;

  constructor(
    options: PropertyFilterOptions = {
      property: null,
      type: null,
      propertyType: null,
      operator: null,
      value: null,
    },
  ) {
    Object.assign(this, options);
  }

  public serialize(): SerializedPropertyFilter {
    return {
      dataGroupId: this.dataGroupId,
      negated: this.negated,
      operator: this.operator,
      property: this.property,
      propertyType: this.propertyType,
      joinPropertyType: this.joinPropertyType,
      scdEvent: this.scdEvent,
      stagedFilterClause: this.stagedFilterClause,
      type: this.type,
      value: this.value as Value,
      customPropertyId: this.customPropertyId,
    };
  }

  public getLabel(): string {
    return this.getFilterLabelParts()
      .map((labelPart) => labelPart.label)
      .join(` `);
  }

  // TODO opslead
  public getFilterLabelParts(): FilterLabelPart[] {
    const operatorString = this.type === Type.Boolean ? `is` : this.operator;
    const valueString = String(this.value);
    let valueParts: string[] = [valueString];
    if (this.value === null) {
      valueParts = [];
    } else if (this.type === Type.Datetime) {
      if (this.operator === DatetimeOperator.Relative || this.operator === DatetimeOperator.RelativeFuture) {
        const {unit, value} = (this.value as SerializedDatetime).window;
        valueParts = [new BehaviorWindow(unit, value).toRelativeRangeString()];
      } else if (this.operator === DatetimeOperator.RelativeBeforeInPast) {
        const {unit, value} = (this.value as SerializedDatetime).window;
        valueParts = [new BehaviorWindow(unit, value).toRelativeRangeString(), `ago`];
        if (value === 1) {
          if (unit === WindowUnit.Hour) {
            valueParts.unshift(`an`);
          } else {
            valueParts.unshift(`a`);
          }
        }
      } else if (Array.isArray(this.value)) {
        valueParts = [
          formatDate(parseDate(this.value[0] as string)),
          `and`,
          formatDate(parseDate(this.value[1] as string)),
        ];
      } else {
        valueParts = [formatDate(parseDate(valueString))];
      }
    } else if (Array.isArray(this.value)) {
      switch (this.type) {
        case Type.Number:
          // filter between two values
          valueParts = [String(this.value[0]), `and`, String(this.value[1])];
          break;
        case Type.String: {
          let valueStringArray = this.value;
          if (this.value.length > MAX_LABEL_ENTRIES) {
            valueStringArray = this.value
              .slice(0, MAX_LABEL_ENTRIES - 1)
              .concat(`${this.value.length - MAX_LABEL_ENTRIES + 1} others`);
          }
          valueParts = valueStringArray.reduce((acc: string[], curr: string, idx, arr) => {
            return idx !== arr.length - 1 ? [...acc, curr, `or`] : [...acc, curr];
          }, []) as string[];
          break;
        }
        default:
          throw new Error(`unexpected array value`);
      }
    }

    const labels = [renamePropertyOld(this.property, {dataGroupId: this.dataGroupId}), operatorString, ...valueParts];
    // Even indexes are highlighted
    return labels.map((label, i) => ({label, isHighlighted: i % 2 === 0}));
  }

  public getStagedFilterClause(): StagedPropertyFilterClause {
    return this.stagedFilterClause;
  }

  public isNegated(): boolean {
    return this.negated;
  }

  public commitStagedFilterClause(): PropertyFilter {
    if (this.hasInvalidStagedFilterClause()) {
      return this;
    }

    return this.newPropertyFilter(this.stagedFilterClause);
  }

  public setNegated(negated: boolean): PropertyFilter {
    return this.newPropertyFilter({negated});
  }

  // TODO opslead
  public setStagedFilterClause(options: Partial<StagedPropertyFilterClause>): PropertyFilter {
    const newStagedClause = {...this.stagedFilterClause, ...options};
    const newType = newStagedClause.type;
    let currentType = null;
    if (this.stagedFilterClause) {
      currentType = this.stagedFilterClause.type;

      if (this.stagedFilterClause.type === Type.Dimension && options.dataGroupId) {
        newStagedClause.property = [
          Array.isArray(this.stagedFilterClause.property)
            ? this.stagedFilterClause.property[0]
            : this.stagedFilterClause.property,
          ...(Array.isArray(newStagedClause.property) ? newStagedClause.property : [newStagedClause.property]),
        ];
        newStagedClause.joinPropertyType = this.stagedFilterClause.joinPropertyType;
        newStagedClause.scdEvent = this.stagedFilterClause.scdEvent;
        newStagedClause.dataGroupId = this.stagedFilterClause.dataGroupId;
      }
    }

    // changing typecast results in new filter operator
    if (!this.stagedFilterClause || newType !== currentType) {
      newStagedClause.operator = options.operator || DEFAULT_FILTER_OPERATOR[newType];
    }

    // changing operator clears staged value
    if (!this.stagedFilterClause || newStagedClause.operator !== this.stagedFilterClause.operator) {
      if (isDefinedValue(options.value)) {
        newStagedClause.value = options.value;
      } else {
        let newValue = null;
        switch (newStagedClause.operator) {
          case BooleanOperator.True:
            newValue = true;
            break;
          case BooleanOperator.False:
            newValue = false;
            break;
          case DatetimeOperator.Relative:
          case DatetimeOperator.RelativeBeforeInPast:
            newValue = {window: {unit: DEFAULT_WINDOW_UNIT, value: DEFAULT_RELATIVE_NUM_DAYS}};
            break;
          case DatetimeOperator.RelativeFuture:
            newValue = {window: {unit: DEFAULT_WINDOW_UNIT, value: -DEFAULT_RELATIVE_NUM_DAYS}};
            break;
          default:
            break;
        }

        newStagedClause.value = newValue;
      }
    }

    const newClause = this.newPropertyFilter();
    newClause.stagedFilterClause = newStagedClause;
    return newClause;
  }

  // TODO opslead
  public getExpressionTree(): OperatorNode {
    let expression = null;
    const type = this.type;
    if (type === Type.Datetime) {
      expression = this.getValueOperatorNode(
        ArithmeticOperator.Datetime,
        valueToSerializedDatetime(this.value, this.operator as DatetimeOperator),
      );
    } else if (Array.isArray(this.value)) {
      switch (this.type) {
        case Type.Number: {
          let greaterThan: Value = null;
          let lessThan: Value = null;
          if (type === Type.Number) {
            greaterThan = Math.min(...(this.value as number[]));
            lessThan = Math.max(...(this.value as number[]));
          } else {
            const [first, second] = this.value.map((v) => parseDate(v as string));
            greaterThan = formatDate(first < second ? first : second, {iso: true});
            lessThan = formatDate(first < second ? second : first, {iso: true});
          }
          expression = this.getBetweenOperatorExpressionTree(greaterThan, lessThan);
          break;
        }
        case Type.String:
          if (
            !this.value.length ||
            (this.operator !== StringOperator.Equals && this.operator !== StringOperator.DoesNotEqual)
          ) {
            throw new Error(`unexpected value array`);
          }

          // string is equal to or not equal to an array of values
          expression = this.getValueOperatorNode(ArithmeticOperator.EqualsAny, this.value);
          if (this.operator === StringOperator.DoesNotEqual) {
            expression = new OperatorNode(LogicalOperator.Not, expression, null);
          }
          break;
        default:
          throw new Error(`unexpected value array`);
      }
    } else {
      if (this.operator === StringOperator.Set || this.operator === StringOperator.NotSet) {
        // no value provided for set/not set operators, and no typecast
        // typecasting an undefined value to string yields the string "undefined", which is always defined
        expression = new OperatorNode(
          this.getOperatorFromFilterOperator(this.operator),
          new OperandNode({
            property: this.propertyType,
            value: this.getOperandValue(),
            ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
            ...(this.joinPropertyType ? {join_property_type: this.joinPropertyType} : {}),
            ...(this.scdEvent ? {join_property_type: this.scdEvent} : {}),
          }),
          null,
        );
      } else {
        expression = this.getValueOperatorNode(this.getOperatorFromFilterOperator(this.operator), this.value);
      }
    }

    return expressionToClauseRoot(conditionalNegateExpression(expression, this.negated));
  }

  public toTrackingData(): Obj {
    return {
      operator: this.operator,
      property: this.property,
      'property type': this.propertyType,
      type: this.type,
      value: this.value,
      customPropertyId: this.customPropertyId,
    };
  }

  public isInvalidForDataGroup(dataGroupId): boolean {
    // this method is currently only accurate for property types with data group information
    // (not for e.g. events, which are always valid for any data group)
    return profilePropertyIsInvalidForDataGroup({
      profilePropertyDataGroupId: this.dataGroupId,
      profilePropertyName: this.property,
      dataGroupId,
    });
  }

  public hasInvalidStagedFilterClause(): boolean {
    const {operator, value, type} = this.stagedFilterClause;
    return isInvalidStagedFilterClause(type, operator, value);
  }

  private getOperandValue(): Value {
    return this.customPropertyId ? {customPropertyId: this.customPropertyId} : this.property;
  }

  private getOperandDataGroupValue(): Value {
    return Array.isArray(this.property) ? this.property[1] : null;
  }

  private getPropertyNode(): ExpressionNode {
    /**
     * Always apply a typecast operator in the tree, even when the user did not explicitly choose a typecast.
     * This has the benefit of capturing the property type information in the tree, allowing us to distinguish
     * between different UI representations for operators such as ==
     */
    return new OperatorNode(
      this.getTypeCastOperatorFromType(this.type),
      new OperandNode({
        property: this.propertyType,
        value: this.getOperandValue(),
        ...(this.getOperandDataGroupValue() ? {data_group_value: this.getOperandDataGroupValue()} : {}),
        ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
        ...(this.joinPropertyType ? {join_property_type: this.joinPropertyType} : {}),
        ...(this.scdEvent ? {join_property_type: this.scdEvent} : {}),
      }),
      null,
    );
  }

  private getValueOperatorNode(operator: Operator, value: Value | Value[]) {
    if (operator === ArithmeticOperator.In || operator === ArithmeticOperator.NotIn) {
      // ensure that output is "<value> in <list/string property>" by having value as left child
      return new OperatorNode(operator, new OperandNode({property: Property.Literal, value}), this.getPropertyNode());
    }
    return new OperatorNode(operator, this.getPropertyNode(), new OperandNode({property: Property.Literal, value}));
  }

  private getBetweenOperatorExpressionTree(greaterThan: Value, lessThan: Value) {
    return new OperatorNode(
      LogicalOperator.And,
      new OperatorNode(
        ArithmeticOperator.GreaterThanEquals,
        this.getPropertyNode(),
        new OperandNode({property: Property.Literal, value: greaterThan}),
      ),
      new OperatorNode(
        ArithmeticOperator.LessThanEquals,
        this.getPropertyNode(),
        new OperandNode({property: Property.Literal, value: lessThan}),
      ),
    );
  }

  private getOperatorFromFilterOperator(filterOperator: FilterOperator): Operator {
    switch (filterOperator) {
      case StringOperator.Equals:
      case NumberOperator.EqualTo:
      case DatetimeOperator.On:
      case BooleanOperator.True:
      case BooleanOperator.False:
        return ArithmeticOperator.Equals;
      case NumberOperator.NotEqualTo:
      case StringOperator.DoesNotEqual:
        return ArithmeticOperator.NotEquals;
      case NumberOperator.GreaterThan:
        return ArithmeticOperator.GreaterThan;
      case NumberOperator.LessThan:
        return ArithmeticOperator.LessThan;
      case NumberOperator.AtLeast:
        return ArithmeticOperator.GreaterThanEquals;
      case NumberOperator.AtMost:
        return ArithmeticOperator.LessThanEquals;
      case StringOperator.Contains:
      case ListOperator.Contains:
        return ArithmeticOperator.In;
      case ListOperator.DoesNotContain:
      case StringOperator.DoesNotContain:
        return ArithmeticOperator.NotIn;
      case StringOperator.Set:
        return ArithmeticOperator.Defined;
      case StringOperator.NotSet:
        return ArithmeticOperator.NotDefined;
      default:
        throw new Error(`Unsupported filter operator ${filterOperator}`);
    }
  }

  private getTypeCastOperatorFromType(type: Type): TypeCastOperator {
    switch (type) {
      case Type.Boolean:
        return TypeCastOperator.Boolean;
      case Type.String:
        return TypeCastOperator.String;
      case Type.Number:
        return TypeCastOperator.Number;
      case Type.List:
        return TypeCastOperator.List;
      case Type.Datetime:
        return TypeCastOperator.Datetime;
      default:
        throw new Error(`Invalid type ${type}`);
    }
  }

  private newPropertyFilter(options: Partial<PropertyFilterOptions> = {}): PropertyFilter {
    const oldOptions: PropertyFilterOptions = {
      dataGroupId: this.dataGroupId,
      negated: this.negated,
      operator: this.operator,
      property: this.property,
      propertyType: this.propertyType,
      joinPropertyType: this.joinPropertyType,
      scdEvent: this.scdEvent,
      type: this.type,
      value: this.value,
      customPropertyId: this.customPropertyId,
    };
    return new PropertyFilter({...oldOptions, ...options});
  }
}
